import React, { useEffect, useState } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import Nav from "./components/Nav/Nav";
import WasteForm from "./components/WasteForm/WasteForm";
import ProgressBar from "./components/ProgressBar/ProgressBar";
import Confirmation from "./components/Confirmation/Confirmation";
import WelcomePage from "./components/WelcomePage/WelcomePage";
import DetailsPage from "./components/DetailsPage/DetailsPage";
import Footer from "./components/Footer/Footer";
import Tooltip from "./components/Tooltip/Tooltip";
import NotFound from "./components/NotFound/NotFound";
import envConfig from "./environment/envConfig";
import DevLogin from "./components/DevLogin/DevLogin";
import { Customer, Event, WasteItem } from "./core/types/types";
import flavourConfig from "./environment/flavourConfig";
import "./styles/styles.css";

const App: React.FC = () => {
  const location = useLocation();

  const [customerTicketNumber, updateCustomerTicketNumber] = useState(0);
  const [eventList, updateEventList] = useState<Event[]>([]);
  const [existingBooking, updateExistingBooking] = useState<WasteItem[]>([]);
  const [hasCompletedCustomerPage, updateHasCompletedCustomerPage] =
    useState(false);
  const [hasCompletedWastePage, updateHasCompletedWastePage] = useState(false);
  const [hasCompletedWelcomePage, updateHasCompletedWelcomePage] =
    useState(false);
  const [hasTickedPrivacyPolicy, updateHasTickedPrivacyPolicy] =
    useState<boolean>(false);
  const [isLoggedIn, updateLogIn] = useState(false);
  const [isResidentialCustomer, updateIsResidentialCustomer] = useState<
    boolean | null
  >(null);
  const [isUpdatingBooking, updateIsUpdatingBooking] = useState(false);
  const [isValidAddress, updateIsValidAddress] = useState<boolean>(false);
  const [progressState, updateProgressState] = useState(0);
  const [showTooltip, updateShowTooltip] = useState(false);

  const [customer, updateCustomer] = useState<Customer>({
    TicketNumber: 0,
    Event: 0,
    TimeSlot: 0,
    CustomerName: "",
    CustomerAddress: "",
    CustomerPhone: "",
    CustomerEmail: "",
  });

  // Updates the progress bar state every time the page location changes
  useEffect(() => {
    let answer = 0;
    switch (location.pathname) {
      case "/":
        break;
      case "/customer-form":
        answer = 1;
        break;
      case "/waste-form":
        answer = 2;
        break;
      case "/confirmation":
        answer = 3;
        break;
    }
    updateProgressState(answer);
  }, [location]);

  return envConfig.requireLogin && !isLoggedIn ? (
    <DevLogin updateLogin={updateLogIn} />
  ) : (
    <div
      className={`bg-${flavourConfig.backgroundImage} bg-cover bg-top bg-fixed bg-no-repeat min-h-screen max-w-screen overflow-x-hidden`}
    >
      <Nav />
      <div className="min-h-screen flex flex-col justify-center pt-32 pb-32 lg:pt-40 lg:pb-40 w-11/12 lg:10/12 xl:9/12 2xl:w-8/12 mx-auto">
        <div className="background-white-ie-fix shadow-lg rounded-lg border border-gray-300">
          <div className="relative xl:w-full pt-10 pb-6 rounded-t-lg">
            <div className="flex items-center w-11/12 mx-auto">
              <ProgressBar
                progressState={progressState}
                isUpdatingBooking={isUpdatingBooking}
              />
              <div className="absolute right-1 top-2 sm:right-3 sm:top-3.5 text-gray-600">
                <Tooltip
                  showTooltip={showTooltip}
                  isUpdatingBooking={isUpdatingBooking}
                  updateShowTooltip={updateShowTooltip}
                  pathname={location.pathname}
                />
              </div>
            </div>
          </div>
          <Switch>
            <Route exact path="/">
              <WelcomePage
                updateEventList={updateEventList}
                updateHasCompletedWelcomePage={updateHasCompletedWelcomePage}
                updateIsUpdatingBooking={updateIsUpdatingBooking}
              />
            </Route>
            <Route exact path="/customer-form">
              <DetailsPage
                eventList={eventList}
                customerTicketNumber={customerTicketNumber}
                updateCustomerTicketNumber={updateCustomerTicketNumber}
                updateExistingBooking={updateExistingBooking}
                customer={customer}
                updateCustomer={updateCustomer}
                isResidentialCustomer={isResidentialCustomer}
                updateIsResidentialCustomer={updateIsResidentialCustomer}
                isValidAddress={isValidAddress}
                updateIsValidAddress={updateIsValidAddress}
                hasCompletedWelcomePage={hasCompletedWelcomePage}
                updateHasCompletedCustomerPage={updateHasCompletedCustomerPage}
                isUpdatingBooking={isUpdatingBooking}
                updateIsUpdatingBooking={updateIsUpdatingBooking}
                hasTickedPrivacyPolicy={hasTickedPrivacyPolicy}
                updateHasTickedPrivacyPolicy={updateHasTickedPrivacyPolicy}
              />
            </Route>
            <Route exact path="/waste-form">
              <WasteForm
                customer={customer}
                updateCustomerTicketNumber={updateCustomerTicketNumber}
                customerTicketNumber={customerTicketNumber}
                existingBooking={existingBooking}
                hasCompletedCustomerPage={hasCompletedCustomerPage}
                updateHasCompletedWastePage={updateHasCompletedWastePage}
                isUpdatingBooking={isUpdatingBooking}
              />
            </Route>
            <Route exact path="/confirmation">
              <Confirmation
                hasCompletedWastePage={hasCompletedWastePage}
                customerTicketNumber={customerTicketNumber}
                isUpdatingBooking={isUpdatingBooking}
              />
            </Route>
            <NotFound />
          </Switch>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default App;
